import { useTripPickupOperators, PickupOperator } from "@/api/trips";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Users } from "lucide-react";

interface PickupOperatorsDialogProps {
  tripId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PickupOperatorsDialog({
  tripId,
  isOpen,
  onOpenChange,
}: PickupOperatorsDialogProps) {
  const { data, isLoading, error } = useTripPickupOperators(tripId);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Pickup Operators
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 max-h-[calc(80vh-120px)]">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="p-4 text-center text-red-500">
              Failed to load pickup operators information
            </div>
          )}

          {data?.operators && (
            <>
              {data.operators.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No pickup operators found for this trip
                </div>
              ) : (
                <div className="space-y-4">
                  {data.operators.map((item: PickupOperator) => (
                    <div
                      key={item.operator.id}
                      className="rounded border p-4 bg-slate-50"
                    >
                      <div className="font-medium text-lg">
                        {item.operator.firstName} {item.operator.lastName}
                      </div>
                      <div className="space-y-3">
                        {item.senderGroups.map((group) => (
                          <div
                            key={group.senderName}
                            className="border-t pt-2 first:border-t-0 first:pt-0"
                          >
                            <div className="text-sm font-medium mb-1">
                              Orders picked up from{" "}
                              {group.senderName || "Unknown Sender"}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {group.orders.map((order) => (
                                <div
                                  key={order.id}
                                  className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-sm"
                                >
                                  #{order.orderNumber}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
